import { useState } from "react";

interface TournamentLogoProps {
  logoUrl?: string;
  emoji?: string;
  alt?: string;
  /** Tailwind size class for the outer tile — default "w-12 h-12". */
  size?: string;
  /** Outer shape: "square" (rounded-sm) or "circle" (rounded-full). */
  shape?: "square" | "circle";
  className?: string;
}

/**
 * Tournament crest with a **white backing tile** so the logo is legible
 * against the Nike-palette black background. Most federation/league
 * crests are designed for light backgrounds and disappear on pure black
 * without a chip behind them.
 *
 * Falls back to the tournament emoji if the image fails to load.
 * Fallback renders *without* the white tile (emoji is more legible
 * against the dark card).
 */
export function TournamentLogo({
  logoUrl,
  emoji = "🏆",
  alt = "",
  size = "w-12 h-12",
  shape = "square",
  className = "",
}: TournamentLogoProps) {
  const [failed, setFailed] = useState(false);
  const showImg = !!logoUrl && !failed;
  const radius = shape === "circle" ? "rounded-full" : "rounded-md";

  if (!showImg) {
    return (
      <div className={`${size} ${radius} flex items-center justify-center bg-secondary/40 shrink-0 ${className}`}>
        <span className="text-2xl leading-none">{emoji}</span>
      </div>
    );
  }

  return (
    <div className={`${size} ${radius} bg-white flex items-center justify-center shrink-0 overflow-hidden ${className}`}>
      <img
        src={logoUrl}
        alt={alt}
        className="w-full h-full object-contain p-1.5"
        onError={() => setFailed(true)}
      />
    </div>
  );
}
